import { Component, inject, Input, OnChanges, OnInit } from '@angular/core';

import { PricingOption } from '../../../../models/pricing-option.model';
import { mapAsBody } from '../../util/request-util';
import { Transporter } from '../../../../models/transporter.model';
import { TransportFormGroup } from '../../../../models/forms/TransportFormGroup';
import { InsuranceProvider } from 'models/enums/insurance-provider.enum';
import { ItemConditionEnum } from '../../../../models/enums/item-condition.enum';
import { InsuranceTransporterConfigurationService } from '../../../../services/insurance-transporter-configuration.service';
import { CurrentDataService } from '../../../../services/current-data-service';
import { NumberUtil } from '../../util/number-util';
import { Transport } from '../../../../models/transport.model';
import { FaDirectiveEnum } from '../../../../models/enums/fa-directive.enum';

@Component({
  selector: 'jhi-insurance-row',
  templateUrl: './insurance-row.component.html',
  styleUrls: ['./insurance-row.component.scss'],
})
export class InsuranceRowComponent implements OnInit, OnChanges {
  private readonly insuranceTransporterConfigurationService = inject(InsuranceTransporterConfigurationService);
  private readonly currentDataService = inject(CurrentDataService);

  @Input() transport!: Transport;
  @Input() transportForm!: TransportFormGroup;
  @Input() optionInsurance?: PricingOption;

  public insuranceIncluded?: boolean;

  public insuranceProvider: InsuranceProvider = InsuranceProvider.AXA;
  public insuranceName = '';
  public insuranceActivated = false;

  protected readonly FaDirectiveEnum = FaDirectiveEnum;

  private transporter?: Transporter;

  public get InsuranceProvider(): typeof InsuranceProvider {
    return InsuranceProvider;
  }

  public get ItemsCondition(): typeof ItemConditionEnum {
    return ItemConditionEnum;
  }

  public ngOnInit(): void {
    this.currentDataService.getCurrentTransporter().subscribe(it => {
      this.transporter = it;
    });

    this.insuranceTransporterConfigurationService
      .retrieveConfiguration(this.transport.transporter!.id!)
      .pipe(mapAsBody)
      .subscribe(value => {
        this.insuranceProvider = value.insurance as InsuranceProvider;
        this.insuranceName = this.insuranceProviderName(this.insuranceProvider);
      });

    this.transportForm.controls.goodsValue.valueChanges.subscribe(value => this.switchInsurance(value));
  }

  public ngOnChanges(): void {
    if (this.optionInsurance?.value) {
      this.insuranceIncluded = true;
      this.insuranceActivated = true;
    } else {
      this.insuranceIncluded = false;
      this.insuranceActivated = this.transportForm.controls.goodsValue.value > 0;
    }
  }

  public switchInsurance(value?: number): void {
    if (typeof value !== 'undefined') {
      this.insuranceActivated = value > 0;
    } else {
      this.insuranceActivated = !this.insuranceActivated;
      if (!this.insuranceActivated) {
        this.transportForm.controls.goodsValue.reset(0);
      }
    }
  }

  private insuranceProviderName(provider: InsuranceProvider): string {
    switch (provider) {
      case InsuranceProvider.AXA:
        return 'Ad Valorem';
      case InsuranceProvider.HELVETIA:
        return 'Transporteurs & Logisticiens';
      case InsuranceProvider.MMA:
        return 'Assurance marchandises transportées MMA';
      case InsuranceProvider.TOKIO_MARINE_HCC:
        return 'Tokio Marine Europe';
      case InsuranceProvider.ALLIANZ_TRANSPORT_PRIVE:
        return 'Allianz Transport privé';
      case InsuranceProvider.ALLIANZ_ASSURFRET:
        return 'Allianz ASSURFRET';
      case InsuranceProvider.GUEMAS:
      case InsuranceProvider.CARAVELLE:
        return 'A MODIFIER';
      case InsuranceProvider.MS_AMLIN:
        return 'Ms Amlin';
      case InsuranceProvider.GENERALI:
        return 'Generali';
      case InsuranceProvider.GAN:
        return 'GAN';
    }
  }

  public getInsuranceMaxValue(): string {
    if (this.insuranceIncluded && this.optionInsurance) {
      return this.optionInsurance.value.toString();
    }
    return NumberUtil.formatNumberToCurrencyString(this.transport.insuranceItem?.maxValue ?? 200000);
  }

  public getInsuranceDescription(): string {
    if (this.insuranceIncluded) {
      return 'insurance-included.insuranceDescription';
    }
    return 'insurance-row.insuranceDescription';
  }

  public getInsuranceCGV(insuranceProvider: InsuranceProvider): string {
    switch (insuranceProvider) {
      case InsuranceProvider.AXA:
        return 'https://storage.googleapis.com/bary-doc-transporters/cg/cg-axa.pdf';
      case InsuranceProvider.HELVETIA:
        return 'https://storage.googleapis.com/bary-doc-transporters/cg/cg-helvetia.pdf';
      case InsuranceProvider.MMA:
        return 'https://storage.googleapis.com/bary-doc-transporters/cg/CGV_mma.pdf';
      case InsuranceProvider.TOKIO_MARINE_HCC:
        return 'https://storage.googleapis.com/bary-doc-transporters/cg/CGV_tokio_marine_europe.pdf';
      case InsuranceProvider.ALLIANZ_TRANSPORT_PRIVE:
        return 'https://storage.googleapis.com/bary-doc-transporters/cg/cg-allianz-transport-prive.pdf';
      case InsuranceProvider.ALLIANZ_ASSURFRET:
        return 'https://storage.googleapis.com/bary-doc-transporters/cg/cg-allianz-assurfret.pdf';
      case InsuranceProvider.GAN:
        return 'https://storage.googleapis.com/bary-doc-transporters/cg/cg-gan.pdf';
      case InsuranceProvider.GUEMAS:
      case InsuranceProvider.CARAVELLE:
        return 'A MODIFIER';
      case InsuranceProvider.MS_AMLIN:
        return 'https://storage.googleapis.com/bary-doc-transporters/cg/cg-amlin.pdf';
      case InsuranceProvider.GENERALI:
        return 'https://storage.googleapis.com/bary-doc-transporters/cg/cg-generali.pdf';
      case InsuranceProvider.GAN:
        return 'https://storage.googleapis.com/bary-doc-transporters/cg/cg-gan.pdf';
    }
  }

  public getInsuranceAdValorem(): string {
    switch (this.transporter?.technicalName) {
      case 'sarrion':
        return 'https://storage.googleapis.com/bary-doc-transporters/cg/cg-sarrion-advalorem.pdf';
      default:
        return 'https://storage.googleapis.com/bary-doc-transporters/cg/cg_advalorem.pdf';
    }
  }
}
